import React, { useEffect, useRef } from "react";

import { localizedText } from "shared/lib/localizedMap";
import { itemsCountLabel } from "shared/lib/menuLabels";
import { Button, ChipToggle, CloseIcon, IconButton, Modal, SearchInput } from "shared/ui";

import { useStopListContext } from "features/StopList/context";

import {
    Chips,
    Controls,
    Empty,
    Footer,
    Hint,
    List,
    LoadMoreAnchor,
    Meta,
    Name,
    Row,
    TitleRow,
} from "./styles";

interface PickerRowProps {
    name: string;
    meta: string;
    justAdded: boolean;
    pending: boolean;
    onToggle: () => void;
}

const PickerRow = ({ name, meta, justAdded, pending, onToggle }: PickerRowProps) => (
    <Row $justAdded={justAdded}>
        <div>
            <Name $justAdded={justAdded}>{name}</Name>
            <Meta>{justAdded ? "В стоп-листе · только что" : meta}</Meta>
        </div>
        {justAdded ? (
            <Button variant="text" size="compact" disabled={pending} onClick={onToggle}>
                Отменить
            </Button>
        ) : (
            <Button
                variant="outlined"
                color="danger"
                size="compact"
                disabled={pending}
                onClick={onToggle}
            >
                Убрать
            </Button>
        )}
    </Row>
);

/*
* Диалог «Убрать из продажи». Изменения применяются по клику, а строка остаётся на месте
* в состоянии «только что убрано» с отменой: во время утренней приёмки убирают подряд
* десяток позиций, и закрывать диалог ради опечатки не должно быть нужно.
* */
export const StopListPicker = () => {
    const state = useStopListContext();
    const { picker } = state;

    const anchorRef = useRef<HTMLDivElement | null>(null);
    // Колбэк в ref: он пересоздаётся на каждый ответ, а наблюдатель должен пережить это
    // без переподписки, иначе на границе списка уходит лишний запрос.
    const loadMoreRef = useRef(picker.loadMore);

    useEffect(() => {
        loadMoreRef.current = picker.loadMore;
    }, [picker.loadMore]);

    useEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor || !picker.hasMore || picker.mode !== "items") return;

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
        });

        observer.observe(anchor);

        return () => observer.disconnect();
        // Число загруженных строк обязательно в зависимостях: наблюдатель сообщает о смене
        // видимости, и без переподписки якорь, оставшийся на экране, второй раз не сработает.
    }, [picker.hasMore, picker.mode, picker.items.length]);

    const renderItems = () => {
        if (picker.itemsError) return <Empty>Не удалось загрузить позиции</Empty>;

        if (picker.items.length === 0) {
            if (picker.itemsLoading) return <Empty>Загружаем позиции…</Empty>;

            return (
                <Empty>
                    {picker.query.trim() !== ""
                        ? "Ничего не найдено"
                        : "Все позиции меню уже в стоп-листе"}
                </Empty>
            );
        }

        return (
            <>
                {picker.items.map((item) => (
                    <PickerRow
                        key={item.id}
                        name={localizedText(item.name, state.locale)}
                        meta={[
                            state.categoryNameById(item.menuCategoryId),
                            state.formatPrice(item.sellPrice.price),
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                        justAdded={picker.justAddedItemIds.has(item.id)}
                        pending={picker.pendingItemIds.has(item.id)}
                        onToggle={() => picker.toggleItem(item)}
                    />
                ))}
                {picker.hasMore && <LoadMoreAnchor ref={anchorRef} />}
            </>
        );
    };

    const renderCategories = () => {
        if (picker.categories.length === 0) {
            return (
                <Empty>
                    {picker.query.trim() !== ""
                        ? "Ничего не найдено"
                        : "Все категории уже в стоп-листе"}
                </Empty>
            );
        }

        return picker.categories.map((category) => (
            <PickerRow
                key={category.id}
                name={localizedText(category.name, state.locale)}
                meta={`${itemsCountLabel(category.itemsCount)} в категории`}
                justAdded={picker.justAddedCategoryIds.has(category.id)}
                pending={picker.pendingCategoryIds.has(category.id)}
                onToggle={() => picker.toggleCategory(category)}
            />
        ));
    };

    return (
        <Modal
            open={state.pickerOpen}
            onClose={state.closePicker}
            fillHeight
            title={
                <TitleRow>
                    <span>Убрать из продажи</span>
                    <IconButton label="Закрыть" onClick={state.closePicker}>
                        <CloseIcon />
                    </IconButton>
                </TitleRow>
            }
            actions={
                <Footer>
                    <Hint>Изменения применяются сразу</Hint>
                    <Button variant="contained" onClick={state.closePicker}>
                        Готово
                    </Button>
                </Footer>
            }
        >
            <Controls>
                <SearchInput
                    value={picker.query}
                    placeholder={
                        picker.mode === "items" ? "Поиск по названию" : "Поиск по категориям"
                    }
                    onChange={picker.setQuery}
                />

                <Chips>
                    <ChipToggle
                        size="small"
                        active={picker.mode === "items"}
                        onClick={() => picker.setMode("items")}
                    >
                        Позиции · {picker.itemsCount}
                    </ChipToggle>
                    <ChipToggle
                        size="small"
                        active={picker.mode === "categories"}
                        onClick={() => picker.setMode("categories")}
                    >
                        Категории · {picker.categoriesCount}
                    </ChipToggle>
                </Chips>
            </Controls>

            <List>{picker.mode === "items" ? renderItems() : renderCategories()}</List>
        </Modal>
    );
};
